import clsx from 'clsx';
import * as React from 'react';
import { ChangeEventHandler, useCallback } from 'react';
import { FieldsWithType } from '@lmt/unicorn-shared/app/helpers/typeUtils';
import { RadioButton } from './RadioButton';
import { Input } from './inputs/Input';
import { createId } from '@lmt/unicorn-shared/app/helpers/create-id';

import styles from './RadioButtonGroup.module.scss';
import { useTranslation } from 'react-i18next';
import { TagsEditor } from './tags/TagsEditor';
import { OnTagsChangeEvent } from './tags/tags-helpers';

export type Props<
  TOption extends Record<Key, string | number>,
  Key extends FieldsWithType<TOption, string | number>,
> = {
  options: TOption[];
  labelField: keyof TOption;
  disabledField?: (item: TOption) => boolean;
  rootClassName?: string;
  disabled?: boolean;
  error?: boolean;
  errorText?: string;
  onEditableFieldChange?: (value: string) => void;
  onSelectedOptionChanged: (newSelectedOption: TOption | null) => void;
  radioButtonClassName?: string;
  inputClassName?: string;
  valueField: Key;
  value?: TOption[Key];
  editableFieldValue?: TOption[Key];
  isMultiline?: boolean;
} & (
  | {
      required: true;
      onSelectedOptionChanged: (newSelectedOption: TOption) => void;
    }
  | {
      required: false;
      onSelectedOptionChanged: (newSelectedOption: TOption | null) => void;
    }
);

export function RadioButtonGroup<
  TOption extends Record<Key, string | number>,
  Key extends FieldsWithType<TOption, string | number>,
>(props: Props<TOption, Key>) {
  type TValue = TOption[Key];
  const {
    required,
    rootClassName,
    options,
    valueField,
    labelField,
    disabled,
    onSelectedOptionChanged,
    error,
    errorText,
    editableFieldValue,
    onEditableFieldChange,
    radioButtonClassName,
    inputClassName,
    isMultiline,
  } = props;

  const i18next = useTranslation();

  const getLabelForOption = useCallback(
    (option: TOption) => option[labelField],
    [],
  );

  const getValueForOption: (option: TOption) => TValue = useCallback(
    (option: TOption) => option[valueField],
    [],
  );

  const onChange = useCallback(
    (e: React.FormEvent<HTMLInputElement>, selectedOption: TOption | null) => {
      if (e.currentTarget.value !== props.value) {
        (onSelectedOptionChanged as (option: TOption | null) => void)(
          selectedOption,
        );
      }
    },
    [options, onSelectedOptionChanged, props.value],
  );
  const editableChange = useCallback(
    (e: OnTagsChangeEvent, value: string) => {
      if (value === undefined) return;
      // select the editable option when user starts to edit it
      if (value && props.editableFieldValue) {
        if (props.value !== props.editableFieldValue) {
          const editableOption = options.find(
            (option) => option[valueField] === props.editableFieldValue,
          );
          if (editableOption) {
            editableOption[labelField] = value as any;
            onEditableFieldChange?.(value);
            (onSelectedOptionChanged as (option: TOption | null) => void)(
              editableOption,
            );
            return;
          }
        }
      }
      onEditableFieldChange?.(value);
    },
    [onEditableFieldChange, props.editableFieldValue, props.value, options],
  );

  return (
    <div
      className={clsx(styles.rootContainer, rootClassName)}
      data-test-id={'radio-group'}
    >
      {!required && (
        <div key="not_selected" className={styles.fieldContainer}>
          <RadioButton
            title={i18next.t('uikit.inputs.nothing_selected')}
            key="not_selected"
            checked={!props.value}
            value={''}
            name={createId() + 'not_selected'}
            disabled={disabled}
            onChange={(e) => {
              onChange(e, null);
            }}
            className={clsx(styles.radioButton, radioButtonClassName)}
          />
        </div>
      )}
      {options.map((option) => {
        const value = getValueForOption(option);
        const label = getLabelForOption(option)?.toString();
        return (
          <div key={value} className={styles.fieldContainer}>
            <RadioButton
              title={value === editableFieldValue ? '' : label}
              key={value}
              checked={props.value === value && !!label}
              value={value}
              name={createId() + value}
              disabled={disabled || (value === editableFieldValue && !label)} // RadioButton is disabled when option is empty
              onChange={(e) => {
                onChange(e, option);
              }}
              className={clsx(styles.radioButton, radioButtonClassName)}
            />
            {value === editableFieldValue && (
              <TagsEditor
                value={label ?? ''}
                onChange={editableChange}
                isMultiline={!!isMultiline}
                className={inputClassName}
              />
            )}
          </div>
        );
      })}
      {error && !!errorText && (
        <div data-error="true" className={styles.errorText}>
          {errorText}
        </div>
      )}
    </div>
  );
}
